import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddCircleOutline,
  CheckCircle,
  ChevronRight,
  Close,
  DocumentScanner,
  Edit,
  ReceiptLong,
  ExpandLess,
  ExpandMore,
  History,
} from '@mui/icons-material';
import { addToSlip, betFromLegs } from '../utils/slipStore';
import { normalizeBook } from '../utils/books';
import {
  BoardScanLine,
  BoardScanResult,
  BoardScanSummary,
  getBoardScan,
  listBoardScans,
  ParlayLeg,
  ProbUncertaintyBand,
  scanBoard,
  SimulationRun,
} from '../services/api';
import { SegmentedToggle } from './SegmentedToggle';
import { bandFor, CONFIDENCE_LABEL, EdgeConfidence, evaluateBet, growthBps, hitsLabel } from '../utils/betValue';
import { bookProb } from '../utils/bookMode';
import { PARLAY_FULL_MESSAGE, parseAmericanOdds } from '../utils/parlay';
import { toAmericanOdds } from './OddsToggle';
import { COLORS, interpolateColor } from '../utils/cellColors';
import { getModalBodyBg } from './DriverDetailsModal';
import { GroupedHistoryList } from './GroupedHistoryList';
import { BoardScanTips } from './BoardScanTips';
import { BoardScanNotices } from './BoardScanNotices';
import { applyMarketPicks, MarketPicks, reviewScan } from '../utils/boardScanReview';
import { MatchupProbs, matchupKey, priceMatchups } from '../utils/boardScanMatchups';
import { BoardScanMatchups } from './BoardScanMatchups';
import { prepareScreenshot } from '../utils/imagePrep';
import { track } from '../utils/track';
import { startTraceAction } from '../utils/traceparent';

const MAX_SCREENSHOTS = 4;
/** Largest file accepted before it's shrunk for upload (the upload itself must be 8 MB or less). */
const MAX_PICK_BYTES = 30_000_000;

type PricedMarket = 'win' | 'top3' | 'top5' | 'top10';
/** Every market a pick can be in: the finishing markets, and head-to-heads (priced from the matchup simulation). */
type PickMarket = PricedMarket | 'h2h';
const MARKET_LABEL: Record<PickMarket, string> = {
  win: 'Win',
  top3: 'Top 3',
  top5: 'Top 5',
  top10: 'Top 10',
  h2h: 'Matchups',
};
/** A pick's name: "Kyle Larson Top 10", or "Kyle Larson over Denny Hamlin" for a matchup. */
const pickName = (p: { line: BoardScanLine; market: PickMarket }) =>
  p.market === 'h2h' ? `${p.line.driver} over ${p.line.opponent}` : `${p.line.driver} ${MARKET_LABEL[p.market]}`;
/** A pick as a parlay / slip leg. */
const pickLeg = (p: { line: BoardScanLine; market: PickMarket }): ParlayLeg =>
  p.market === 'h2h'
    ? { kind: 'ahead_of', driver: p.line.driver!, others: [p.line.opponent!] }
    : { kind: 'top_n', driver: p.line.driver!, n: MARKET_N[p.market] };
const MARKET_N: Record<PricedMarket, number> = {
  win: 1,
  top3: 3,
  top5: 5,
  top10: 10,
};
const CONF_COLOR: Record<EdgeConfidence, 'success' | 'warning' | 'default' | 'error'> = {
  clear: 'success',
  marginal: 'warning',
  within: 'default',
  negative: 'error',
};
const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const fmtOdds = (o: number) => (o > 0 ? `+${o}` : `${o}`);
const pct = (p: number) => `${(p * 100).toFixed(1)}%`;
// Theme gradient (yellow → green) for positive values; factor 0..1.
const valueSx = (factor: number) => ({
  backgroundColor: interpolateColor(COLORS.mid, COLORS.max, Math.min(1, Math.max(0, factor))),
  color: 'black',
  fontWeight: 500,
});
// Edge scaled by our uncertainty (3x the band is full green); EV per $100 (+$25 is full green).
const edgeSx = (p: { shown: { edgePts: number; bandPts: number } }) =>
  valueSx(p.shown.bandPts > 0 ? p.shown.edgePts / p.shown.bandPts / 3 : 1);
const logit = (p: number) => Math.log(p / (1 - p));
// A price this far (log-odds) from what books typically post for our probability is more likely a misread than value.
const IMPLAUSIBLE_GAP = 1.6;
const STAKE_UNIT_KEY = 'ltb.stakeUnit';

type ModelName = 'base' | 'layered';
const MODEL_LABEL: Record<ModelName, string> = { base: 'Base', layered: 'Track Specialists' };

interface Pick {
  key: string;
  line: BoardScanLine;
  market: PickMarket;
  odds: number;
  base: ReturnType<typeof evaluateBet>;
  layered?: ReturnType<typeof evaluateBet>;
  pBase: number;
  pLayered?: number;
  implausible: boolean;
  /** Models with the edge: clear/marginal ones for picks, any positive edge for the smaller-edges list. */
  models: ModelName[];
  /** The evaluation shown: the stronger of the models with the edge. */
  shown: ReturnType<typeof evaluateBet>;
  pShown: number;
  /** Expected bankroll growth (basis points per bet) at the suggested stake: the ranking key. */
  growth: number;
  /** The same on our own probability (no margin taken off): ranks the edges inside our margin. */
  rawGrowth: number;
  tier: 'pick' | 'small' | 'none';
}

interface BoardScanDialogProps {
  open: boolean;
  onClose: () => void;
  run: SimulationRun;
  uncertainty?: ProbUncertaintyBand[];
  onAddLeg: (leg: ParlayLeg) => void;
  /** The sidebar parlay is full: the add-to-parlay buttons are disabled, with the reason on hover. */
  parlayFull?: boolean;
}

/** Upload sportsbook screenshots; get the best-value picks by our model, priced from the simulation on screen. */
export const BoardScanDialog: React.FC<BoardScanDialogProps> = ({
  open,
  onClose,
  run,
  uncertainty,
  onAddLeg,
  parlayFull,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BoardScanResult | null>(null);
  // Previous scans: the list, and the saved scan being viewed (its track and time replace the live run's).
  const [view, setView] = useState<'scan' | 'history'>('scan');
  const [history, setHistory] = useState<BoardScanSummary[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ site: string; scannedAt: string } | null>(null);
  const [edits, setEdits] = useState<Record<string, number>>({});
  // the user's market for a board section (when none was shown, or the printed one looked wrong)
  const [marketPicks, setMarketPicks] = useState<MarketPicks>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [market, setMarket] = useState<'all' | PickMarket>('all');
  const [bothOnly, setBothOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showSmall, setShowSmall] = useState(false);
  const [stakeUnit, setStakeUnit] = useState<'pct' | 'units'>(() => {
    try {
      return window.localStorage.getItem(STAKE_UNIT_KEY) === 'units' ? 'units' : 'pct';
    } catch {
      return 'pct';
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(STAKE_UNIT_KEY, stakeUnit);
    } catch {
      // preference only
    }
  }, [stakeUnit]);
  // 1 unit = 1% of bankroll.
  const fmtStake = (stake: number) =>
    stake <= 0 ? '–' : stakeUnit === 'units' ? `${(stake * 100).toFixed(1)}u` : `${(stake * 100).toFixed(2)}%`;
  const [added, setAdded] = useState<Set<string>>(new Set());
  // Our chances for the board's head-to-heads (null: none on the board), and whether pricing them failed.
  const [matchups, setMatchups] = useState<MatchupProbs | null>(null);
  const [matchupsFailed, setMatchupsFailed] = useState(false);
  // A scan (or a saved one) shows once its matchups are priced too, so nothing pops in afterwards.
  const priceAndShow = async (scanned: BoardScanResult) => {
    let probs: MatchupProbs | null = null;
    let failed = false;
    try {
      probs = await priceMatchups(scanned.lines, run);
    } catch (e) {
      console.warn('board scan: pricing matchups', e);
      failed = true;
    }
    setMatchups(probs);
    setMatchupsFailed(failed);
    setResult(scanned);
  };
  // Lines added to the Slip Builder (by line and price, so a corrected price can be added again).
  const [onSlip, setOnSlip] = useState<Set<string>>(new Set());
  const hasLayered = !!run.layered;
  const theme = useTheme();
  const bodyBg = getModalBodyBg(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const reset = () => {
    setFiles([]);
    setResult(null);
    setSaved(null);
    setView('scan');
    setError(null);
    setEdits({});
    setMarketPicks({});
    setMatchups(null);
    setMatchupsFailed(false);
    setAdded(new Set());
    setShowAll(false);
    setShowSmall(false);
  };

  const showHistory = async () => {
    setView('history');
    setHistoryError(null);
    try {
      setHistory(await listBoardScans());
    } catch (e) {
      setHistoryError((e as Error).message);
    }
  };

  const openSaved = async (scanId: string) => {
    setOpening(scanId);
    setHistoryError(null);
    try {
      const detail = await getBoardScan(scanId);
      reset();
      await priceAndShow(detail);
      setMarketPicks({});
      setSaved({ site: detail.site, scannedAt: detail.scannedAt });
    } catch (e) {
      setHistoryError((e as Error).message);
    } finally {
      setOpening(null);
    }
  };

  // A saved scan for another track can't be added to this track's parlay.
  const canAddLegs = !saved || saved.site === run.site;

  // Only PNG, JPEG and WebP screenshots, up to 4, and none so big the browser would struggle to shrink it; anything
  // left out is said so (the backend checks again, including the pixel size).
  const addFiles = useCallback((list: FileList | File[]) => {
    const all = Array.from(list);
    const typed = all.filter((f) => /^image\/(png|jpeg|webp)$/.test(f.type));
    const sized = typed.filter((f) => f.size <= MAX_PICK_BYTES);
    setFiles((prev) => {
      const next = [...prev, ...sized].slice(0, MAX_SCREENSHOTS);
      const problems: string[] = [];
      if (typed.length < all.length) problems.push('Only PNG, JPEG or WebP screenshots can be scanned.');
      if (sized.length < typed.length) problems.push(`Screenshots must be under ${MAX_PICK_BYTES / 1_000_000} MB.`);
      if (prev.length + sized.length > MAX_SCREENSHOTS) problems.push(`Up to ${MAX_SCREENSHOTS} screenshots per scan.`);
      setError(problems.length ? problems.join(' ') : null);
      return next;
    });
  }, []);

  // Paste a screenshot straight from the clipboard.
  useEffect(() => {
    if (!open || result) return;
    const onPaste = (e: Event) => {
      const items = Array.from((e as ClipboardEvent).clipboardData?.files ?? []);
      if (items.length) addFiles(items);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open, result, addFiles]);

  const scan = async () => {
    setScanning(true);
    setError(null);
    try {
      const layeredBy = new Map((run.layered?.drivers ?? []).map((d) => [d.driver, d]));
      const field = run.drivers.map((d) => {
        const l = layeredBy.get(d.driver);
        return {
          driver: d.driver,
          base: {
            win: d.winProb,
            top3: d.top3Prob,
            top5: d.top5Prob,
            top10: d.top10Prob,
          },
          // our odds shaped like a book's, so the server can tell an unlabeled section's market by its prices
          book: {
            win: bookProb(d.winProb, 'win'),
            top3: bookProb(d.top3Prob, 'top3'),
            top5: bookProb(d.top5Prob, 'top5'),
            top10: bookProb(d.top10Prob, 'top10'),
          },
          ...(l && {
            layered: {
              win: l.winProb,
              top3: l.top3Prob,
              top5: l.top5Prob,
              top10: l.top10Prob,
            },
          }),
        };
      });
      // smaller uploads (same pixels for the reader) so they get through on a phone connection
      const prepared = await Promise.all(files.map(prepareScreenshot));
      const started = performance.now();
      startTraceAction();
      const scanned = await scanBoard(prepared, { site: run.site, asOf: run.asOf, configName: run.configName }, field);
      track('action', `scan board (${prepared.length} screenshot${prepared.length === 1 ? '' : 's'})`, performance.now() - started);
      await priceAndShow(scanned);
      setMarketPicks({});
    } catch (e) {
      // fetch throws a TypeError when the upload itself drops ("Load failed" in Safari, "Failed to fetch" in Chrome)
      setError(
        e instanceof TypeError
          ? "The upload didn't finish (the connection dropped). Check your signal and try again."
          : (e as Error).message,
      );
    } finally {
      setScanning(false);
    }
  };

  const lineKey = (l: BoardScanLine) =>
    `${l.market}|${l.driver ?? l.driverRaw}|${l.opponent ?? ''}${l.market === 'unknown' ? `|${l.group}` : ''}`;

  // the lines with the user's market picks applied, and what to tell them about the read
  const lines = useMemo(
    () => (result ? applyMarketPicks(result.lines, marketPicks, run) : []),
    [result, marketPicks, run],
  );
  const review = useMemo(
    () => (result ? reviewScan(result.lines, marketPicks, lines) : null),
    [result, marketPicks, lines],
  );

  const picks: Pick[] = useMemo(() => {
    if (!result) return [];
    const out: Pick[] = [];
    for (const l of lines) {
      if (l.status !== 'ok' || !l.driver) continue;
      // Matchups take our chance of the driver finishing ahead from the matchup simulation; the rest from the run.
      const isMatchup = l.market === 'h2h' && !!l.opponent;
      if (!isMatchup && (!(l.market in MARKET_N) || l.probBase === undefined)) continue;
      const m: PickMarket = isMatchup ? 'h2h' : (l.market as PricedMarket);
      const mk = isMatchup ? matchupKey(l.driver, l.opponent!) : '';
      const pBase = isMatchup ? matchups?.base[mk] : l.probBase;
      if (pBase === undefined) continue;
      const key = lineKey(l);
      const odds = edits[key] ?? l.odds;
      if (!(pBase > 0 && pBase < 1)) continue;
      const base = evaluateBet(pBase, odds, bandFor(uncertainty, m, pBase), 0.25);
      const pLayered = isMatchup ? matchups?.layered?.[mk] : l.probLayered;
      const layered =
        pLayered !== undefined && pLayered > 0 && pLayered < 1
          ? evaluateBet(pLayered, odds, bandFor(uncertainty, m, pLayered), 0.25)
          : undefined;
      // Last misread guard: a price far from anything a book would normally post for this driver.
      const implausible =
        Math.abs(logit(base.implied) - logit(bookProb(pBase, m))) > IMPLAUSIBLE_GAP && !(key in edits);
      const good = (c: EdgeConfidence) => c === 'clear' || c === 'marginal';
      const ratio = (v: ReturnType<typeof evaluateBet>) => (v.bandPts > 0 ? v.edgePts / v.bandPts : v.edgePts);
      const evals: [ModelName, ReturnType<typeof evaluateBet>, number][] = [['base', base, pBase]];
      if (layered && pLayered !== undefined) evals.push(['layered', layered, pLayered]);
      let withEdge = evals.filter(([, v]) => good(v.confidence));
      const tier = withEdge.length > 0 ? 'pick' : evals.some(([, v]) => v.edgePts > 0) ? 'small' : 'none';
      if (tier === 'small') withEdge = evals.filter(([, v]) => v.edgePts > 0);
      // Expected bankroll growth at the suggested stake: edge balanced against how often the bet hits.
      const growth = (v: ReturnType<typeof evaluateBet>, prob: number) => growthBps(prob, odds, v.bandPts);
      // Edges inside our margin grow nothing on the cautious probability, so they're compared on our own probability.
      const rawGrowth = (prob: number) => growthBps(prob, odds, 0);
      const [, shown, pShown] = (withEdge.length ? withEdge : evals).reduce((a, b) => {
        const gb = growth(b[1], b[2]);
        const ga = growth(a[1], a[2]);
        if (gb !== ga) return gb > ga ? b : a;
        const rb = rawGrowth(b[2]);
        const ra = rawGrowth(a[2]);
        if (rb !== ra) return rb > ra ? b : a;
        return ratio(b[1]) > ratio(a[1]) ? b : a;
      });
      out.push({
        key,
        line: l,
        market: m,
        odds,
        base,
        layered,
        pBase,
        pLayered,
        implausible,
        models: withEdge.map(([m]) => m),
        shown,
        pShown,
        growth: growthBps(pShown, odds, shown.bandPts),
        rawGrowth: growthBps(pShown, odds, 0),
        tier,
      });
    }
    // Rank by expected bankroll growth at the suggested stake, which balances edge against how often a bet hits. Edge
    // alone (raw EV, or even edge relative to our margin in points) favors longshots: their margins are small in
    // points but large relative to their chance, and they're where books load margin and our odds are least sure.
    // Edges inside our margin all grow nothing on the cautious probability, so they're ranked the same way on our own
    // probability (the "smaller edges" list); edge relative to the margin only breaks exact ties.
    return out.sort(
      (a, b) =>
        b.growth - a.growth ||
        b.rawGrowth - a.rawGrowth ||
        b.shown.edgePts / b.shown.bandPts - a.shown.edgePts / a.shown.bandPts,
    );
  }, [result, lines, edits, uncertainty, matchups]);

  const visible = picks.filter(
    (p) =>
      (market === 'all' || p.market === market) &&
      (!bothOnly || p.models.length === 2) &&
      p.tier === 'pick' &&
      !p.implausible,
  );
  // Positive edges inside our uncertainty: shown collapsed below the picks.
  const small = picks.filter(
    (p) =>
      (market === 'all' || p.market === market) &&
      (!bothOnly || p.models.length === 2) &&
      p.tier === 'small' &&
      !p.implausible,
  );
  // (lines priced cautiously are left out the same way, but the summary card already says why)
  const flagged = picks.filter((p) => p.implausible && p.line.marketBasis !== 'cautious');
  const counts = useMemo(() => {
    const c: Partial<Record<BoardScanLine['status'], number>> = {};
    for (const l of lines) c[l.status] = (c[l.status] ?? 0) + 1;
    return c;
  }, [lines]);
  const skipped = lines.length - (counts.ok ?? 0);

  const priceView = (p: Pick) =>
    editing === p.key ? (
      <TextField
        size="small"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => saveEdit(p.key)}
        onKeyDown={(e) => e.key === 'Enter' && saveEdit(p.key)}
        sx={{ width: 90 }}
      />
    ) : (
      <>
        {fmtOdds(p.odds)}
        <Tooltip title="Fix this price if it was misread">
          <IconButton
            size="small"
            onClick={() => {
              setEditing(p.key);
              setDraft(fmtOdds(p.odds));
            }}
            sx={{ ml: 0.25, p: 0.25 }}
          >
            <Edit sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </>
    );

  const addButton = (p: Pick) =>
    !canAddLegs ? null : (
      <Tooltip
        title={added.has(p.key) ? 'In your parlay' : parlayFull ? PARLAY_FULL_MESSAGE : 'Add to parlay'}
      >
        <span>
          <IconButton
            size="small"
            disabled={added.has(p.key) || !!parlayFull}
            onClick={() => {
              onAddLeg(pickLeg(p));
              setAdded((s) => new Set(s).add(p.key));
            }}
          >
            {added.has(p.key) ? (
              <CheckCircle color="primary" fontSize="small" />
            ) : (
              <AddCircleOutline fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );

  // Add to Slip Builder: shown on row hover (always on touch screens), and pinned once the line is on the slip. Only
  // for scans of the track on screen, since the browser keeps one slip per track.
  const slipKey = (p: Pick) => `${p.key}|${p.odds}`;
  const slipButton = (p: Pick) => {
    if (!canAddLegs) return null;
    const on = onSlip.has(slipKey(p));
    return (
      <Tooltip title={on ? 'On your Slip Builder slip' : 'Add to Slip Builder'}>
        <IconButton
          size="small"
          className={on ? undefined : 'slip-add'}
          aria-label={on ? `${pickName(p)} is on your slip` : `Add ${pickName(p)} to Slip Builder`}
          onClick={() => {
            if (on) return;
            const bet = betFromLegs([pickLeg(p)], fmtOdds(p.odds));
            if (!bet) return;
            addToSlip(run.site, { ...bet, book: normalizeBook(result?.book) });
            setOnSlip((x) => new Set(x).add(slipKey(p)));
          }}
        >
          <ReceiptLong fontSize="small" color={on ? 'primary' : undefined} />
        </IconButton>
      </Tooltip>
    );
  };
  // Row hover reveals the slip button (the same treatment as the favorite stars in the results table).
  const hoverRevealSx = {
    '& .slip-add': { opacity: 0, transition: 'opacity 0.15s' },
    '&:hover .slip-add, & .slip-add:focus-visible': { opacity: 1 },
    '@media (hover: none)': { '& .slip-add': { opacity: 1 } },
  };

  const pickList = (list: Pick[]) =>
    isMobile ? (
      <Stack spacing={1}>
        {list.map((p) => (
          <Box key={p.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {pickName(p)}
                </Typography>
                {hasLayered && (
                  <Typography
                    variant="caption"
                    color={p.models.length === 2 ? 'primary.main' : 'text.secondary'}
                    sx={{ display: 'block' }}
                  >
                    {p.models.length === 2 ? 'Both models' : MODEL_LABEL[p.models[0] ?? 'base']}
                  </Typography>
                )}
              </Box>
              <Chip size="small" color={CONF_COLOR[p.shown.confidence]} label={CONFIDENCE_LABEL[p.shown.confidence]} />
              {slipButton(p)}
              {addButton(p)}
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                mt: 1,
                fontVariantNumeric: 'tabular-nums',
                '& .lbl': { display: 'block', color: 'text.secondary', fontSize: 11 },
              }}
            >
              <Typography variant="body2" component="div" sx={{ whiteSpace: 'nowrap' }}>
                <span className="lbl">Price</span>
                {priceView(p)}
              </Typography>
              <Typography variant="body2" component="div">
                <span className="lbl">Ours</span>
                {toAmericanOdds(p.pShown)}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  hits {hitsLabel(p.pShown)}
                </Typography>
              </Typography>
              <Typography variant="body2" component="div">
                <span className="lbl">Edge</span>
                <Box component="span" sx={{ ...edgeSx(p), px: 0.75, py: 0.25, borderRadius: 1, whiteSpace: 'nowrap' }}>
                  +{p.shown.edgePts.toFixed(1)} pts
                </Box>
              </Typography>
              <Typography variant="body2" component="div">
                <span className="lbl">Stake</span>
                {fmtStake(p.shown.stake)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    ) : (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pick</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Ours</TableCell>
              <TableCell align="right">Hits</TableCell>
              <TableCell align="right">Edge</TableCell>
              <TableCell align="right">EV / $100</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell align="right">Stake</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((p) => (
              <TableRow key={p.key} hover sx={hoverRevealSx}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {pickName(p)}
                  </Typography>
                  {hasLayered && (
                    <Typography
                      variant="caption"
                      color={p.models.length === 2 ? 'primary.main' : 'text.secondary'}
                      sx={{ display: 'block' }}
                    >
                      {p.models.length === 2 ? 'Both models' : MODEL_LABEL[p.models[0] ?? 'base']}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'nowrap',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {priceView(p)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pct(p.pShown)}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {toAmericanOdds(p.pShown)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {hitsLabel(p.pShown)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    ...edgeSx(p),
                  }}
                >
                  +{p.shown.edgePts.toFixed(1)} pts
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                  }}
                >
                  +${p.shown.ev100.toFixed(2)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={CONF_COLOR[p.shown.confidence]}
                    label={CONFIDENCE_LABEL[p.shown.confidence]}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtStake(p.shown.stake)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {slipButton(p)}
                  {addButton(p)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );

  const saveEdit = (key: string) => {
    const o = parseAmericanOdds(draft);
    if (o !== null) setEdits((e) => ({ ...e, [key]: o }));
    setEditing(null);
  };

  return (
    <Dialog
      open={open}
      onClose={() => !scanning && onClose()}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: 2, backgroundImage: 'none', border: 1, borderColor: 'divider' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocumentScanner color="primary" />
        <Box sx={{ flex: 1 }}>
          Scan a board
          <Typography variant="body2" color="text.secondary">
            {saved
              ? `${saved.site} · scanned ${fmtWhen(saved.scannedAt)} · priced as of that scan`
              : `${run.site} · priced from the simulation on screen`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={scanning} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: bodyBg }}>
        {view === 'history' ? (
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center">
              <Typography sx={{ fontWeight: 600, flex: 1 }}>Previous scans</Typography>
              <Button size="small" onClick={reset}>
                New scan
              </Button>
            </Stack>
            {historyError && <Alert severity="error">{historyError}</Alert>}
            {history === null && !historyError ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : history && history.length === 0 ? (
              <Alert severity="info">No saved scans yet. Boards you scan while signed in show up here.</Alert>
            ) : (
              <GroupedHistoryList
                items={history ?? []}
                groupKey={(h) => h.site}
                itemKey={(h) => h.scanId}
                renderItem={(h) => (
                  <ButtonBase
                    onClick={() => openSaved(h.scanId)}
                    disabled={!!opening}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      gap: 1.5,
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {h.site}
                        {h.book ? ` · ${h.book}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtWhen(h.scannedAt)} · {h.nLines} lines read, {h.nPriced} priced
                      </Typography>
                    </Box>
                    {opening === h.scanId ? <CircularProgress size={18} /> : <ChevronRight color="action" />}
                  </ButtonBase>
                )}
              />
            )}
          </Stack>
        ) : !result ? (
          <Stack spacing={2}>
            <BoardScanTips />
            <Box
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addFiles(e.dataTransfer.files);
              }}
              component="label"
              sx={{
                display: 'block',
                border: '1.5px dashed',
                borderColor: (t) => alpha(t.palette.primary.main, 0.5),
                bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.06 : 0.04),
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'background-color 150ms, border-color 150ms',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.12 : 0.08),
                },
              }}
            >
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                hidden
                onChange={(e) => e.target.files && addFiles(e.target.files)}
              />
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                  color: 'primary.main',
                }}
              >
                <DocumentScanner sx={{ fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontWeight: 600, mt: 1 }}>Drop, paste or choose sportsbook screenshots</Typography>
              <Typography variant="body2" color="text.secondary">
                Win, top 3, top 5, top 10 and matchup boards for this race. Up to 4 images.
              </Typography>
            </Box>
            {files.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {files.map((f, i) => (
                  <Chip
                    key={i}
                    label={f.name}
                    variant="outlined"
                    color="primary"
                    onDelete={() => setFiles((p) => p.filter((_, j) => j !== i))}
                  />
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
              Scans are saved to improve our models. Crop out balances, usernames or bet slips before uploading.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <Button
              variant="contained"
              size="large"
              disabled={files.length === 0 || scanning}
              onClick={scan}
              startIcon={scanning ? <CircularProgress size={18} color="inherit" /> : <DocumentScanner />}
            >
              {scanning ? 'Reading the board… (10–20 seconds)' : 'Find the best value'}
            </Button>
            <Button
              size="small"
              startIcon={<History />}
              onClick={showHistory}
              disabled={scanning}
              sx={{ alignSelf: 'center' }}
            >
              Previous scans
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <SegmentedToggle
                aria-label="Market"
                size="small"
                value={market}
                onChange={setMarket}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'win', label: 'Win' },
                  { value: 'top3', label: 'Top 3' },
                  { value: 'top5', label: 'Top 5' },
                  { value: 'top10', label: 'Top 10' },
                  ...(matchups ? [{ value: 'h2h' as const, label: 'Matchups' }] : []),
                ]}
              />
              {hasLayered && (
                <Chip
                  label="Edge under both models"
                  color={bothOnly ? 'primary' : 'default'}
                  variant={bothOnly ? 'filled' : 'outlined'}
                  onClick={() => setBothOnly((v) => !v)}
                  icon={bothOnly ? <CheckCircle /> : undefined}
                />
              )}
              <Box sx={{ flex: 1 }} />
              <SegmentedToggle
                aria-label="Stake shown as"
                size="small"
                value={stakeUnit}
                onChange={setStakeUnit}
                options={[
                  { value: 'pct', label: '%' },
                  { value: 'units', label: 'Units' },
                ]}
              />
              <Button size="small" onClick={showHistory}>
                Previous scans
              </Button>
              <Button size="small" onClick={reset}>
                Scan another
              </Button>
            </Stack>

            {review && (
              <BoardScanNotices
                review={review}
                site={run.site}
                onPick={(group, m) => setMarketPicks((p) => ({ ...p, [group]: m }))}
              />
            )}

            {(counts.ok ?? 0) === 0 ? (
              // nothing priced: the notices above say why, or the board has only markets we don't price
              !review?.shaky && (
                <Alert severity="info">
                  Nothing on this board is a market we price (win, top 3, top 5, top 10 or head-to-heads for this race).
                </Alert>
              )
            ) : visible.length === 0 ? (
              <Alert severity="info">
                No line on this board beats our uncertainty
                {bothOnly ? ' under both models' : ''}
                {market !== 'all' ? ` in ${MARKET_LABEL[market]}` : ''}. That's normal: real value is rare.
              </Alert>
            ) : (
              pickList(visible)
            )}

            {small.length > 0 && (
              <Box>
                <Button
                  size="small"
                  onClick={() => setShowSmall((v) => !v)}
                  endIcon={showSmall ? <ExpandLess /> : <ExpandMore />}
                  sx={{ color: 'text.secondary' }}
                >
                  {small.length} smaller edge{small.length === 1 ? '' : 's'} within our uncertainty
                </Button>
                <Collapse in={showSmall} unmountOnExit>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Priced better than our odds, but by less than our model's margin of error: closer to a coin flip
                    than real value. No stake is suggested.
                  </Typography>
                  {pickList(small)}
                </Collapse>
              </Box>
            )}

            {flagged.length > 0 && (
              <Alert severity="warning">
                {flagged.length} price{flagged.length === 1 ? '' : 's'} looked unusual for that driver and{' '}
                {flagged.length === 1 ? 'was' : 'were'} left out of the picks:{' '}
                {flagged.map((p) => `${pickName(p)} ${fmtOdds(p.odds)}`).join(', ')}. Check
                them against your book.
              </Alert>
            )}

            {matchupsFailed && lines.some((l) => l.market === 'h2h' && l.status === 'ok') && (
              <Alert severity="warning">
                The head-to-heads on this board couldn't be priced right now (the matchup simulation didn't run). The
                other markets are priced as usual; scan again in a minute to price the matchups.
              </Alert>
            )}

            {(market === 'all' || market === 'h2h') && (
              <BoardScanMatchups
                hasLayered={hasLayered}
                sides={picks
                  .filter((p) => p.market === 'h2h')
                  .map((p) => ({
                    key: p.key,
                    driver: p.line.driver!,
                    opponent: p.line.opponent!,
                    odds: p.odds,
                    pBase: p.pBase,
                    pLayered: p.pLayered,
                    confidence: p.shown.confidence,
                    edgePts: p.shown.edgePts,
                    tier: p.tier,
                    implausible: p.implausible,
                    actions: (
                      // the cards show both buttons all the time (no row hover to reveal them on)
                      <Stack direction="row">
                        {slipButton(p)}
                        {addButton(p)}
                      </Stack>
                    ),
                  }))
                  // in the board's order
                  .sort((x, y) => lines.findIndex((l) => lineKey(l) === x.key) - lines.findIndex((l) => lineKey(l) === y.key))}
              />
            )}

            <Box>
              <Button
                size="small"
                onClick={() => setShowAll((v) => !v)}
                endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
                sx={{ color: 'text.secondary' }}
              >
                {lines.length} lines read
                {result.book ? ` from ${result.book}` : ''} · {counts.ok ?? 0} priced · {skipped} skipped
              </Button>
              <Collapse in={showAll}>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Line</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Ours</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map((l) => {
                        const p = picks.find((x) => x.key === lineKey(l));
                        return (
                          <TableRow key={`${lineKey(l)}|${l.group ?? ''}`}>
                            <TableCell>
                              {l.driver ?? l.driverRaw}{' '}
                              {l.market in MARKET_N
                                ? MARKET_LABEL[l.market as PricedMarket]
                                : l.market === 'h2h'
                                  ? `over ${l.opponent ?? '?'}`
                                  : l.market === 'unknown'
                                    ? '(market not shown)'
                                    : l.marketLabel || l.market}
                              {l.opponent && l.market !== 'h2h' ? ` vs ${l.opponent}` : ''}
                            </TableCell>
                            <TableCell align="right">{fmtOdds(p?.odds ?? l.odds)}</TableCell>
                            <TableCell align="right">
                              {p ? pct(p.pBase) : l.probBase !== undefined ? pct(l.probBase) : '–'}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color={l.status === 'ok' ? 'text.secondary' : 'warning.main'}
                              >
                                {l.status === 'ok'
                                  ? p
                                    ? CONFIDENCE_LABEL[p.shown.confidence] + (p.implausible ? ' · unusual price' : '')
                                    : l.market === 'h2h'
                                      ? "not priced: the matchup simulation didn't run"
                                      : 'priced'
                                  : (l.reason ?? l.status)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Prices as read from your screenshot; check them against your book. Picks are ranked by how much they'd
              grow a bankroll at the suggested stake, which weighs the edge against how often the bet hits. Stakes are ¼ Kelly{stakeUnit === 'units' ? ' (1 unit = 1% of bankroll)' : ''}. Decision
              support from model estimates, not betting advice.
              {!saved && !result.imagesSaved && ' Your screenshot was not stored.'}
              {saved &&
                !canAddLegs &&
                " This scan is for a different track than your simulation, so picks can't be added to its parlay."}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
